import { useState, useEffect } from 'react';
import { useLoyalty } from '../../../contexts/LoyaltyContext';
import { apiGet } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { ContestPlayModal } from './ContestPlayModal';

interface Contest {
  id: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  contestType: string;
  type?: string; // legacy compat
  pointsReward?: number;
  startDate: string;
  endDate: string;
  status: string;
  config: Record<string, unknown>;
  entryConfig: Record<string, unknown>;
  winValidation?: string;
  rulesUrl?: string | null;
}

interface LoyaltyGameArenaProps {
  title?: string;
  showSidebar?: boolean;
  maxItems?: number;
}

type ArenaTab = 'available' | 'completed';

function LoginCta() {
  return (
    <div className="loyalty-card p-8 text-center">
      <div className="text-4xl mb-3 opacity-50">&#127918;</div>
      <h2 className="loyalty-heading text-xl mb-2 text-[var(--color-text)]">
        Login to play challenges
      </h2>
      <p className="text-[var(--color-text-muted)] text-sm mb-4">
        Sign in to compete in weekly contests and earn rewards.
      </p>
      <a
        href="/login"
        className="inline-block px-6 py-2 rounded-lg bg-[var(--color-primary)] text-white font-semibold text-sm hover:opacity-90 transition-opacity"
      >
        Sign In
      </a>
    </div>
  );
}

const TYPE_LABELS: Record<string, string> = {
  instant_win: 'INSTANT WIN',
  draw: 'DRAW',
  leaderboard: 'LEADERBOARD',
  quiz: 'WEEKLY QUIZ',
  predictor: 'PREDICTOR',
  trivia: 'TRIVIA',
  challenge: 'CHALLENGE',
  poll: 'POLL',
  bracket: 'BRACKET',
};

const TYPE_CTA: Record<string, string> = {
  instant_win: 'Play Now',
  draw: 'Enter Draw',
  leaderboard: 'View Leaderboard',
  quiz: 'Play Now',
  predictor: 'Submit Picks',
  trivia: 'Play Now',
  challenge: 'Join Challenge',
  poll: 'Vote Now',
  bracket: 'Make Picks',
};

const GRADIENT_COLORS = [
  'from-purple-600 to-blue-500',
  'from-pink-600 to-orange-500',
  'from-teal-600 to-green-500',
  'from-indigo-600 to-violet-500',
  'from-rose-600 to-red-500',
  'from-amber-600 to-yellow-500',
];

function formatCountdown(endDate: string): string {
  const diff = new Date(endDate).getTime() - Date.now();
  if (diff <= 0) return 'ENDED';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days > 0) return `${days}d left`;
  if (hours > 0) return `${hours}h left`;
  return 'Ending soon';
}

function ContestCard({
  contest,
  index,
  onPlay,
}: {
  contest: Contest;
  index: number;
  onPlay: (c: Contest) => void;
}) {
  const isCompleted = contest.status === 'completed';
  const gradient = GRADIENT_COLORS[index % GRADIENT_COLORS.length];
  const cType = contest.contestType ?? contest.type ?? 'instant_win';
  const typeLabel = TYPE_LABELS[cType] ?? cType.replace(/_/g, ' ').toUpperCase();
  const ctaLabel = TYPE_CTA[cType] ?? 'Play Now';

  return (
    <div className="loyalty-card overflow-hidden group hover:shadow-lg transition-shadow">
      {/* Image / gradient area */}
      <div className={`relative h-36 bg-gradient-to-br ${gradient}`}>
        {contest.imageUrl && (
          <img
            src={contest.imageUrl}
            alt={contest.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {/* Category tag */}
        <div className="absolute top-3 left-3">
          <span className="loyalty-tag-skew text-[10px] font-bold uppercase px-2.5 py-1 rounded text-white bg-black/50 backdrop-blur-sm">
            {typeLabel}
          </span>
        </div>
        {/* Countdown badge */}
        <div className="absolute top-3 right-3">
          <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white">
            {formatCountdown(contest.endDate)}
          </span>
        </div>
        {/* Points badge */}
        {contest.pointsReward && (
          <div className="absolute bottom-3 right-3">
            <span className="text-xs font-bold px-2 py-1 rounded-full bg-[var(--color-primary)] text-white">
              +{contest.pointsReward} PTS
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="loyalty-heading text-sm text-[var(--color-text)] mb-1 line-clamp-1">
          {contest.title}
        </h3>
        {contest.description && (
          <p className="text-xs text-[var(--color-text-muted)] mb-3 line-clamp-2">
            {contest.description}
          </p>
        )}
        {isCompleted ? (
          <span className="inline-block text-xs font-semibold text-[var(--color-text-muted)] px-3 py-1.5 rounded-lg border border-[var(--color-surface-border)]">
            Completed
          </span>
        ) : (
          <button
            onClick={() => onPlay(contest)}
            className="w-full text-xs font-semibold px-3 py-2.5 rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 active:scale-[0.98] transition-all"
          >
            {ctaLabel}
          </button>
        )}
      </div>
    </div>
  );
}

export function LoyaltyGameArenaBlock({ props }: { props: LoyaltyGameArenaProps }) {
  const { isAuthenticated } = useAuth();
  const { data, loading: loyaltyLoading } = useLoyalty();
  const title = props.title ?? 'Game Arena';
  const maxItems = props.maxItems ?? 6;

  const [contests, setContests] = useState<Contest[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<ArenaTab>('available');
  const [playingContest, setPlayingContest] = useState<Contest | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    setLoading(true);

    const pid = data?.programId;
    if (!pid) return;
    apiGet<Contest[]>(`/customer/loyalty/contests?programId=${pid}`)
      .then((res) => {
        if (!cancelled) setContests(Array.isArray(res) ? res : []);
      })
      .catch(() => { if (!cancelled) setContests([]); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [isAuthenticated, data?.programId]);

  if (loyaltyLoading || loading) {
    return (
      <div className="loyalty-card p-6 animate-pulse">
        <div className="h-6 w-40 bg-[var(--color-surface-border)] rounded mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-52 bg-[var(--color-surface-border)] rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) return <LoginCta />;

  const available = contests.filter((c) => c.status !== 'completed');
  const completed = contests.filter((c) => c.status === 'completed');
  const displayContests = (activeTab === 'available' ? available : completed).slice(0, maxItems);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="loyalty-heading text-xl text-[var(--color-text)]">{title}</h2>
        <div className="flex gap-1 bg-[var(--color-surface)] rounded-lg p-1 border border-[var(--color-surface-border)]">
          {([
            { key: 'available' as ArenaTab, label: 'AVAILABLE GAMES' },
            { key: 'completed' as ArenaTab, label: 'COMPLETED' },
          ]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                activeTab === tab.key
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Contest grid */}
      {displayContests.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {displayContests.map((contest, i) => (
            <ContestCard
              key={contest.id}
              contest={contest}
              index={i}
              onPlay={setPlayingContest}
            />
          ))}
        </div>
      ) : (
        <div className="loyalty-card p-8 text-center text-[var(--color-text-muted)] text-sm">
          {activeTab === 'available'
            ? 'No active games right now. Check back soon!'
            : 'No completed games yet.'}
        </div>
      )}

      {/* Play modal */}
      {playingContest && (
        <ContestPlayModal
          contest={playingContest}
          onClose={() => setPlayingContest(null)}
        />
      )}
    </div>
  );
}
